import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { AudioVariantSummary, CatalogSong, LyricTrackSummary, UserSummary } from "@vocab1600/shared";
import { hasAnyRole, teachingRoles } from "@vocab1600/shared";
import { api } from "./api.js";
import { formatTime, toPlayItem, useAudioPlayer } from "./player-context.js";

/**
 * 单曲页（正在播放）。
 *
 * 布局参考主流音乐应用：左栏是封面与曲目信息、旋律版本选择、本歌单词；
 * 右栏是歌词，已校准的版本逐行高亮并自动滚动。传输控制交给底部的全局控制条，
 * 这一页不再自己维护播放状态，避免出现多个播放器实例。
 */
export function SongPage({ user }: { user: UserSummary }) {
  const { songNumber = "" } = useParams();
  const navigate = useNavigate();
  const player = useAudioPlayer();
  const [variants, setVariants] = useState<AudioVariantSummary[] | null>(null);
  const [track, setTrack] = useState<LyricTrackSummary | null>(null);
  const [openWord, setOpenWord] = useState<string | null>(null);
  const lyricsRef = useRef<HTMLDivElement | null>(null);

  const song: CatalogSong | undefined = useMemo(
    () => (window.APP_SONGS ?? []).find((item) => item.num === songNumber),
    [songNumber]
  );

  useEffect(() => {
    if (!songNumber) return;
    setVariants(null);
    api.songVariants(songNumber)
      .then((data) => setVariants(data.variants))
      .catch(() => setVariants([]));
  }, [songNumber]);

  // 时间轴按版本存放，切换版本要重新拉取；没有已发布轨道时回落到静态歌词
  const activeKey = player.state.current?.variant.variantKey ?? null;
  useEffect(() => {
    if (!activeKey) { setTrack(null); return; }
    let cancelled = false;
    api.lyrics(activeKey)
      .then((data) => { if (!cancelled) setTrack(data); })
      .catch(() => { if (!cancelled) setTrack(null); });
    return () => { cancelled = true; };
  }, [activeKey]);

  const playingThisSong = player.state.current?.songNumber === songNumber;
  const position = playingThisSong ? player.state.positionMs : 0;

  const currentLine = useMemo(() => {
    if (!track || !playingThisSong) return -1;
    return track.lines.findIndex((line) => position >= line.startMs && position < line.endMs);
  }, [track, position, playingThisSong]);

  useEffect(() => {
    if (currentLine < 0) return;
    const container = lyricsRef.current;
    const row = container?.querySelector<HTMLElement>(`[data-lyric-index="${currentLine}"]`);
    if (container && row) {
      container.scrollTo({ top: row.offsetTop - container.clientHeight / 2 + row.clientHeight / 2, behavior: "smooth" });
    }
  }, [currentLine]);

  if (!song) {
    return <div className="page">
      <p className="muted">找不到编号为 {songNumber} 的歌曲。</p>
      <button className="ghost-button" onClick={() => navigate("/songs")}>返回歌单</button>
    </div>;
  }

  const canTeach = hasAnyRole(user.roles, teachingRoles);
  const totalLines = song.sections.reduce((n, s) => n + s.lines.length, 0);

  return <div className="now-playing" style={{ "--song-hue": song.hue } as React.CSSProperties}>
    <button className="np-back" onClick={() => navigate("/songs")}>← 歌单</button>

    <div className="np-grid">
      <aside className="np-left">
        <div className="np-cover">
          <span className="np-icon">{song.unitIcon}</span>
          <b className="np-num">{song.num}</b>
          <i className="np-glow" />
        </div>
        <h1 className="np-title">{song.title}</h1>
        <p className="np-sub">{song.unitName} · {song.words.length} 词 · {song.bpm ?? "—"} BPM</p>

        <div className="np-variants">
          <span className="np-label">旋律版本</span>
          {variants === null && <p className="muted small">正在加载…</p>}
          {variants?.length === 0 && <p className="muted small">该歌曲暂无可播放版本（音频未上传或未通过质检）。</p>}
          <div className="np-chips">
            {variants?.map((variant) => {
              const active = player.isCurrent(variant.variantKey);
              return <button
                key={variant.variantKey}
                className={`np-chip${active ? " active" : ""}`}
                onClick={() => player.toggle(toPlayItem(song, variant))}
              >
                <b>{variant.variantCode}</b>
                <small>{formatTime(variant.durationMs)}</small>
                {active && player.state.playing && <i className="np-eq"><s /><s /><s /></i>}
              </button>;
            })}
          </div>
        </div>

        <div className="np-words">
          <span className="np-label">本歌单词　<em className="muted small">点一下看中文</em></span>
          <div className="word-chips">
            {song.words.map((word) => (
              <button
                key={word.id}
                className={`word-chip${openWord === word.id ? " open" : ""}`}
                onClick={() => setOpenWord((prev) => (prev === word.id ? null : word.id))}
              >
                {openWord === word.id ? word.zh : word.en}
              </button>
            ))}
          </div>
        </div>
      </aside>

      <section className="np-lyrics">
        <div className="np-lyrics-head">
          <h2>歌词</h2>
          {track
            ? <span className="pill ok">逐行同步 · {track.variantKey}</span>
            : <span className="pill">{totalLines} 行</span>}
        </div>

        <div className="np-lyrics-body" ref={lyricsRef}>
          {track ? (
            track.lines.map((line, index) => (
              <p
                key={line.lineKey}
                data-lyric-index={index}
                className={`np-line${index === currentLine ? " current" : ""}${index < currentLine ? " past" : ""}`}
                onClick={() => player.seek(line.startMs)}
                title="点击跳到这一行"
              >
                {line.text}
              </p>
            ))
          ) : (
            song.sections.map((section, index) => (
              <div key={`${section.name}-${index}`} className="np-section">
                <h4>{section.name}</h4>
                {section.lines.map((line) => <p key={line.key} className="np-line static">{line.raw}</p>)}
              </div>
            ))
          )}
        </div>

        {!track && (
          <div className="np-hint">
            {activeKey && playingThisSong
              ? `版本 ${activeKey} 还没有校准时间轴，因此暂无逐行高亮。`
              : "播放任意版本后，已校准的版本会自动逐行高亮。"}
            {canTeach && (
              <button className="link-button" onClick={() => navigate(`/studio/${song.num}`)}>去校准</button>
            )}
          </div>
        )}
      </section>
    </div>
  </div>;
}
